import { useState } from "react";
import { BadgeCheck, Banknote, Copy, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { StatusBanner } from "@/components/StatusBanner";
import { formatAmount } from "@/features/billing/api/cashPaymentApi";

const CODE_LENGTH = 8;

const Heading = ({ icon: Icon, tone, title, children }) => (
  <div className="flex items-start gap-3">
    <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone}`} />
    <div><p className="font-medium">{title}</p><p className="mt-1 text-sm text-muted-foreground">{children}</p></div>
  </div>
);

const Row = ({ label, children, testId }) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
    <dt className="mw-eyebrow">{label}</dt>
    <dd data-testid={testId} className="min-w-0 text-sm">{children}</dd>
  </div>
);

function CodeForm({ cash }) {
  const [code, setCode] = useState("");
  const submit = () => {
    if (code.length !== CODE_LENGTH || cash.action.busy) return;
    const value = code;
    setCode("");
    cash.verify(value);
  };
  return (
    <div className="space-y-2">
      <label htmlFor="cash-code" className="mw-eyebrow">Verification code</label>
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          id="cash-code"
          data-testid="cash-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, CODE_LENGTH))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="XXXXXXXX"
          className="h-12 w-full min-w-0 rounded-md border border-input bg-card px-3 font-mono text-base uppercase tracking-[0.3em] outline-none focus:border-foreground sm:flex-1"
        />
        <button type="button" onClick={submit} disabled={code.length !== CODE_LENGTH || cash.action.busy} data-testid="cash-verify-button" className="mw-btn mw-btn-primary">
          {cash.action.busy && <span className="mw-spinner mw-spinner-light" aria-hidden="true" />}
          {cash.action.busy ? "Verifying" : "Verify Payment"}
        </button>
      </div>
    </div>
  );
}

function Pending({ cash }) {
  const ref = cash.tx.id;
  const copy = () => navigator.clipboard?.writeText(ref).then(() => toast.success("Reference copied"), () => toast.error("Couldn't copy the reference"));
  return (
    <div data-testid="publish-entitlement-status" data-state="pending" className="space-y-6">
      <Heading icon={Banknote} tone="text-gold" title="Cash payment requested">
        Pay {formatAmount(cash.tx)} in cash to the MetWorth team, quoting your payment reference. You'll receive an 8-character verification code.
      </Heading>
      <dl className="space-y-4 rounded-md border border-border bg-muted/40 p-4">
        <Row label="Amount" testId="cash-amount">{formatAmount(cash.tx)}</Row>
        <Row label="Payment reference">
          <span className="flex items-center gap-2">
            <span data-testid="cash-reference" className="break-all font-mono text-xs">{ref}</span>
            <button type="button" onClick={copy} data-testid="cash-copy-reference-button" aria-label="Copy reference" className="mw-icon-btn shrink-0 border-border"><Copy className="h-4 w-4" /></button>
          </span>
        </Row>
        <Row label="Status" testId="cash-status">Awaiting cash payment</Row>
      </dl>
      <CodeForm cash={cash} />
    </div>
  );
}

function Initial({ cash }) {
  return (
    <div data-testid="publish-entitlement-status" data-state="initial" className="space-y-5">
      <Heading icon={Lock} tone="text-gold" title="Activate your MetWorth">
        QAR 50 one-time activation. Activation is completed by cash payment to the MetWorth team.
      </Heading>
      <button type="button" onClick={cash.request} disabled={cash.action.busy} data-testid="cash-request-button" className="mw-btn mw-btn-primary">
        {cash.action.busy && <span className="mw-spinner mw-spinner-light" aria-hidden="true" />}
        {cash.action.busy ? "Requesting" : "Request Cash Payment"}
      </button>
    </div>
  );
}

export function CashActivationPanel({ cash }) {
  let body;
  if (cash.phase === "loading") body = <div data-testid="publish-entitlement-status" data-state="loading" className="flex items-center gap-3"><span className="mw-spinner" /><span className="mw-eyebrow">Checking activation</span></div>;
  else if (cash.phase === "error") body = <div data-testid="publish-entitlement-status" data-state="error" className="space-y-4"><StatusBanner>{cash.error}</StatusBanner><button type="button" onClick={cash.load} data-testid="cash-retry-button" className="mw-btn mw-btn-ghost">Try again</button></div>;
  else if (cash.phase === "activated") body = <div data-testid="publish-entitlement-status" data-state="activated"><Heading icon={BadgeCheck} tone="text-emerald-700" title="MetWorth activated">QAR 50 payment verified. You're ready to publish.</Heading></div>;
  else body = cash.phase === "pending" ? <Pending cash={cash} /> : <Initial cash={cash} />;
  return (
    <div className="space-y-4">
      {body}
      {cash.action.error && <StatusBanner testId="cash-error">{cash.action.error}</StatusBanner>}
      {cash.phase === "pending" && <p className="flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Your code is checked securely and never stored on this device.</p>}
    </div>
  );
}
